/**
 * Canonical station-maintenance normaliser.
 *
 * The station-detail API can hand back the maintenance override under
 * any of four shapes depending on which endpoint (list, markers,
 * detail, preview) served the request:
 *
 *   { maintenance_mode: true, maintenance_reason: '…', maintenance_started_at: '…' }
 *   { maintenanceMode: true, maintenanceReason: '…', maintenanceStartedAt: '…' }
 *   { verified_data: { maintenance_mode: true, … } }
 *   { verifiedData: { maintenance_mode: true, … } }
 *
 * Every consumer should route through this helper instead of open-
 * coding the same ??-chain in different files — that's how the
 * non-preview branch of EvProfile silently dropped the flag on the
 * floor while the preview branch handled it correctly.
 *
 * Returns a normalised object with:
 *   maintenanceMode: boolean          (default false)
 *   maintenanceReason: string | null  (trimmed empty coerced to null)
 *   maintenanceStartedAt: string | null
 */

export const EMPTY_MAINTENANCE = Object.freeze({
    maintenanceMode: false,
    maintenanceReason: null,
    maintenanceStartedAt: null,
});

function firstDefined(...vals) {
    for (const v of vals) {
        if (v !== undefined && v !== null) return v;
    }
    return undefined;
}

/**
 * Normalise the maintenance projection off any station-shaped payload.
 * Accepts the raw API response, the nested `verified_data`, or a
 * shape that has already been through another normaliser.
 */
export function normaliseStationMaintenance(source) {
    if (!source || typeof source !== 'object') return { ...EMPTY_MAINTENANCE };

    const vd = source.verified_data || source.verifiedData || {};

    const mode = firstDefined(
        source.maintenance_mode,
        source.maintenanceMode,
        vd.maintenance_mode,
        vd.maintenanceMode,
    );

    const reasonRaw = firstDefined(
        source.maintenance_reason,
        source.maintenanceReason,
        vd.maintenance_reason,
        vd.maintenanceReason,
    );
    // Trim + coerce empty to null — the API stores '' the same as
    // NULL, and the blocked screen renders the reason block only when
    // there is real copy to show.
    const reason = typeof reasonRaw === 'string'
        ? (reasonRaw.trim() === '' ? null : reasonRaw)
        : (reasonRaw ?? null);

    const startedAt = firstDefined(
        source.maintenance_started_at,
        source.maintenanceStartedAt,
        vd.maintenance_started_at,
        vd.maintenanceStartedAt,
    ) ?? null;

    return {
        maintenanceMode: mode === true,
        maintenanceReason: reason,
        maintenanceStartedAt: startedAt,
    };
}

/**
 * Convenience predicate for the render-order gate in EvProfile.
 */
export function isStationUnderMaintenance(source) {
    return normaliseStationMaintenance(source).maintenanceMode === true;
}
